/* V4 Onboarded Home — lightweight starter home users land on after picking
 * a workflow on Step 5. Layout literally mirrors V4Wrapper:
 *   • fixed logo (top-left) + top-right glass panel (date · theme · bell · avatar)
 *   • fixed sidebar glass panel (collapsible, locked except Home)
 *   • fixed centre glass panel that houses the chat (default) OR the
 *     "briefing" view (locked / unlocked section tiles)
 *   • a top "starter home" banner above everything
 * The chat lives in the centre panel exactly like V4's Board-Meeting-Prep
 * view. Sections unlock live the moment the agent's integration connects.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Sun, Moon, Bell, ChevronDown, ChevronLeft, ArrowRight, ArrowLeft, Lock, Sparkles,
  Home, History as HistoryIcon, Bot, GitBranch, BookOpen, Plug,
  Calendar as CalendarIcon, Inbox as InboxIcon, ClipboardList, Megaphone,
  Kanban, Ticket, DollarSign, Users, Share2, PanelLeftClose, PanelLeftOpen,
  FileText, Brain, Check, Loader2, Paperclip, Mic, Send, Zap, AlertTriangle, Eye,
  Activity, TrendingUp, Headphones, ShieldAlert, Heart, Wallet, UserPlus, Smile,
  Mail, BarChart3, Target, CheckCircle,
} from 'lucide-react';
import { getScript } from '../../data/v4OnboardingScripts';
import { SetupModal } from './V4OnboardingFlow';

/* ─── Section config ─── *
 * One section = one card-type renderer. The layout configs below pick from
 * these by `key`. Each section has a soft empty/placeholder copy used when
 * no payload has been unlocked yet.
 */
const SECTION_CFG = {
  inbox:        { label: 'Inbox Snapshot',     icon: Mail,           accent: undefined,  cardType: 'inbox',
                  emptyCopy: 'Nothing here yet. Connect more tools to populate.' },
  calendar:     { label: 'Meetings',           icon: CalendarIcon,   accent: undefined,  cardType: 'meeting',
                  emptyCopy: 'Nothing here yet. Connect more tools to populate.' },
  tasks:        { label: 'Must-Do',            icon: Zap,            accent: 'amber',    cardType: 'task',
                  emptyCopy: 'Nothing here yet. Connect more tools to populate.' },
  campaigns:    { label: 'Campaigns',          icon: BarChart3,      accent: 'amber',    cardType: 'campaign',
                  emptyCopy: 'Nothing here yet. Connect more tools to populate.' },
  pipeline:     { label: 'Active Deals',       icon: Target,         accent: 'amber',    cardType: 'deal',
                  emptyCopy: 'Nothing here yet. Connect more tools to populate.' },
  tickets:      { label: 'Open Tickets',       icon: Ticket,         accent: 'red',      cardType: 'ticket',
                  emptyCopy: 'Nothing here yet. Connect more tools to populate.' },
  finance:      { label: 'Financial Overview', icon: Wallet,         accent: 'amber',    cardType: 'financial',
                  emptyCopy: 'Nothing here yet. Connect more tools to populate.' },
  roster:       { label: 'People & Team',      icon: Users,          accent: 'amber',    cardType: 'people',
                  emptyCopy: 'Nothing here yet. Connect more tools to populate.' },
  // CEO-only placeholder sections (stay empty in onboarding by design).
  ceoTasks:     { label: 'Must-Do',            icon: Zap,            accent: 'amber',    cardType: 'task',
                  emptyCopy: 'Nothing here yet. Connect more tools to populate.' },
  risks:        { label: 'Risks Flagged',      icon: AlertTriangle,  accent: 'red',      cardType: 'placeholder',
                  emptyCopy: 'Invite your team to unlock — risks surface as agents work across departments.' },
  approvals:    { label: 'Pending Approvals',  icon: Check,          accent: undefined,  cardType: 'placeholder',
                  emptyCopy: 'Invite your team to unlock — each invite gets their own seat and agents.' },
  // Empty-only sections in dept layouts (Phase 1 — never populated by onboarding workflows).
  leadAlert:    { label: 'Lead Alert',         icon: AlertTriangle,  accent: 'red',      cardType: 'placeholder',
                  emptyCopy: 'Nothing here yet. Connect more tools to populate.' },
  pipelineAlert:{ label: 'Pipeline Alert',     icon: AlertTriangle,  accent: 'red',      cardType: 'placeholder',
                  emptyCopy: 'Nothing here yet. Connect more tools to populate.' },
  churnAlert:   { label: 'Churn Alert',        icon: AlertTriangle,  accent: 'red',      cardType: 'placeholder',
                  emptyCopy: 'Nothing here yet. Connect more tools to populate.' },
  revenueAlert: { label: 'Revenue Alert',      icon: AlertTriangle,  accent: 'red',      cardType: 'placeholder',
                  emptyCopy: 'Nothing here yet. Connect more tools to populate.' },
  burnoutRisk:  { label: 'Burnout Risk',       icon: AlertTriangle,  accent: 'red',      cardType: 'placeholder',
                  emptyCopy: 'Nothing here yet. Connect more tools to populate.' },
  leadMetrics:  { label: 'Lead Metrics',       icon: TrendingUp,     accent: 'emerald',  cardType: 'placeholder',
                  emptyCopy: 'Nothing here yet. Connect more tools to populate.' },
  pipelineMetrics:{ label: 'Pipeline Metrics', icon: TrendingUp,     accent: 'emerald',  cardType: 'placeholder',
                  emptyCopy: 'Nothing here yet. Connect more tools to populate.' },
  supportMetrics:{ label: 'Support Metrics',   icon: TrendingUp,     accent: 'emerald',  cardType: 'placeholder',
                  emptyCopy: 'Nothing here yet. Connect more tools to populate.' },
  financeMetrics:{ label: 'Finance Metrics',   icon: TrendingUp,     accent: 'emerald',  cardType: 'placeholder',
                  emptyCopy: 'Nothing here yet. Connect more tools to populate.' },
  peopleMetrics:{ label: 'People Metrics',     icon: TrendingUp,     accent: 'emerald',  cardType: 'placeholder',
                  emptyCopy: 'Nothing here yet. Connect more tools to populate.' },
  needsDecision:{ label: 'Needs Decision',     icon: CheckCircle,    accent: 'amber',    cardType: 'placeholder',
                  emptyCopy: 'Nothing here yet. Connect more tools to populate.' },
};

/* ─── Layout configs ─── */
const CEO_LAYOUT = {
  cols: 3,
  columns: [
    [{ key: 'inbox' }, { key: 'calendar' }],
    [{ key: 'risks' }, { key: 'tasks' }],
    [{ key: 'approvals' }],
  ],
};

const DEPT_LAYOUTS = {
  marketing: {
    cols: 4,
    columns: [
      [{ key: 'inbox' },        { key: 'calendar' }],
      [{ key: 'tasks', overrideLabel: "Today's Tasks" }, { key: 'leadAlert' }],
      [{ key: 'campaigns' },    { key: 'leadMetrics' }],
      [{ key: 'needsDecision' }],
    ],
  },
  sales: {
    cols: 4,
    columns: [
      [{ key: 'inbox' },        { key: 'calendar' }],
      [{ key: 'tasks', overrideLabel: "Today's Tasks" }, { key: 'pipelineAlert' }],
      [{ key: 'pipeline' },     { key: 'pipelineMetrics' }],
      [{ key: 'needsDecision' }],
    ],
  },
  support: {
    cols: 3,
    columns: [
      [{ key: 'inbox' },        { key: 'calendar' }],
      [{ key: 'tasks', overrideLabel: "Today's Tasks" }, { key: 'churnAlert' }],
      [{ key: 'tickets' },      { key: 'supportMetrics' }],
    ],
  },
  finance: {
    cols: 3,
    columns: [
      [{ key: 'inbox' },        { key: 'calendar' }],
      [{ key: 'tasks', overrideLabel: "Today's Tasks" }, { key: 'revenueAlert' }],
      [{ key: 'finance' },      { key: 'financeMetrics' }],
    ],
  },
  people: {
    cols: 3,
    columns: [
      [{ key: 'inbox' },        { key: 'calendar' }],
      [{ key: 'tasks', overrideLabel: "Today's Tasks" }, { key: 'burnoutRisk' }],
      [{ key: 'roster' },       { key: 'peopleMetrics' }],
    ],
  },
};

/* ─── Helpers ─── */
const getHomeActions = (script) => {
  if (!script) return [];
  if (Array.isArray(script.homeActions)) return script.homeActions;
  if (script.homeAction) return [script.homeAction];
  return [];
};

/* CEO mapper — collapses dept-specific section payloads into the
 * CEO 3-section scope (inbox / calendar / tasks). */
const ceoMapper = (section, payload) => {
  switch (section) {
    case 'calendar':
    case 'inbox':
    case 'tasks':
      return { section, payload };
    case 'campaigns':
      return { section: 'tasks', payload: { tasks: (payload.campaigns || []).map((c) => ({
        title: `Review: ${c.name}`, subtitle: `${c.audience} · ${c.channels.join(' · ')}`, tag: c.stage,
      })) } };
    case 'pipeline':
      return { section: 'tasks', payload: { tasks: (payload.deals || []).map((d) => ({
        title: `Review: ${d.name}`, subtitle: `${d.stage} · ${d.value}`, tag: 'Deal',
      })) } };
    case 'tickets':
      return { section: 'inbox', payload: { items: (payload.tickets || []).map((tk) => ({
        from: tk.id, subject: tk.subject, preview: tk.status, tag: tk.priority,
      })) } };
    case 'finance':
      return { section: 'tasks', payload: { tasks: (payload.items || []).map((it) => ({
        title: `Review: ${it.label}`, subtitle: it.status, tag: it.amount,
      })) } };
    case 'roster':
      return { section: 'tasks', payload: { tasks: (payload.items || []).map((it) => ({
        title: it.label, subtitle: it.subtitle, tag: it.status,
      })) } };
    case 'social':
      return { section: 'tasks', payload: { tasks: (payload.posts || []).map((p) => ({
        title: `Review: ${p.text}`, subtitle: `${p.channel} · ${p.status}`, tag: 'Social',
      })) } };
    default:
      return { section, payload };
  }
};

/* Build the unlocked-sections map from script + dept (CEO mapper applied
 * if dept === 'default'). Multiple homeActions can land in the same
 * section — payloads are merged (e.g. tickets→inbox + script-native
 * inbox in one merge). */
const buildUnlockedSections = (script, isDefault) => {
  const out = {};
  const actions = getHomeActions(script);
  for (const ha of actions) {
    const mapped = isDefault ? ceoMapper(ha.section, ha.payload) : { section: ha.section, payload: ha.payload };
    const existing = out[mapped.section];
    if (!existing) {
      out[mapped.section] = mapped.payload;
      continue;
    }
    // Merge arrays under the same primary key (events/tasks/items/etc.)
    out[mapped.section] = mergePayloads(existing, mapped.payload);
  }
  return out;
};

const mergePayloads = (a, b) => {
  const merged = { ...a };
  for (const k of Object.keys(b)) {
    if (Array.isArray(a[k]) && Array.isArray(b[k])) merged[k] = [...a[k], ...b[k]];
    else if (Array.isArray(b[k])) merged[k] = b[k];
    else merged[k] = b[k];
  }
  return merged;
};

const AGENTS = [
  { id: 'luc',    name: 'Luc',    role: 'Chief of Staff', thumb: '/Thumbnails/Luc.png'   },
  { id: 'marsi',  name: 'Marsi',  role: 'Marketing',      thumb: '/Thumbnails/Marsi.png' },
  { id: 'sally',  name: 'Sally',  role: 'Sales',          thumb: '/Thumbnails/Sally.png' },
  { id: 'zira',   name: 'Zira',   role: 'Support',        thumb: '/Thumbnails/Ziira.png' },
  { id: 'finn',   name: 'Finn',   role: 'Finance',        thumb: '/Thumbnails/Finn.png'  },
  { id: 'tali',   name: 'Tali',   role: 'People',         thumb: '/Thumbnails/Tali.png'  },
  { id: 'stark',  name: 'Stark',  role: 'Strategy',       thumb: '/Thumbnails/Stark.png' },
  { id: 'dora',   name: 'Dora',   role: 'Research',       thumb: '/Thumbnails/Dora.png'  },
  { id: 'wright', name: 'Wright', role: 'Copywriting',    thumb: '/Thumbnails/Wright.png'},
  { id: 'buzz',   name: 'Buzz',   role: 'Social Media',   thumb: '/Thumbnails/Buzz.png'  },
];
const findAgent = (id) => AGENTS.find((a) => a.id === id) || AGENTS[0];

/* Department → sidebar nav layout. Mirrors V4Sidebar.NAV_BY_ROLE so every
 * role still sees its full department nav — just locked. The `default`
 * (Founder / CEO with no specific dept selected) gets the CEO nav so they
 * see Org Pulse + all 5 department headers in the sidebar. */
const SIDEBAR_BY_DEPT = {
  marketing: [
    { id: 'home', label: 'Home', icon: Home, active: true },
    { type: 'divider' },
    { id: 'mkt-campaigns', label: 'Campaigns', icon: Megaphone },
    { id: 'mkt-social',    label: 'Social',    icon: Share2 },
    { id: 'mkt-content',   label: 'Content',   icon: ClipboardList },
    { id: 'mkt-analytics', label: 'Analytics', icon: TrendingUp },
  ],
  sales: [
    { id: 'home', label: 'Home', icon: Home, active: true },
    { type: 'divider' },
    { id: 'sales-deals',    label: 'Deals',    icon: Kanban },
    { id: 'sales-pipeline', label: 'Pipeline', icon: Kanban },
    { id: 'sales-outreach', label: 'Outreach', icon: InboxIcon },
    { id: 'sales-reports',  label: 'Reports',  icon: TrendingUp },
  ],
  support: [
    { id: 'home', label: 'Home', icon: Home, active: true },
    { type: 'divider' },
    { id: 'support-tickets',   label: 'Tickets',    icon: Ticket },
    { id: 'support-churn',     label: 'Churn Risk', icon: ShieldAlert },
    { id: 'support-sentiment', label: 'Sentiment',  icon: Heart },
  ],
  finance: [
    { id: 'home', label: 'Home', icon: Home, active: true },
    { type: 'divider' },
    { id: 'finance-cashflow', label: 'Cashflow', icon: DollarSign },
    { id: 'finance-budgets',  label: 'Budgets',  icon: Wallet },
    { id: 'finance-invoices', label: 'Invoices', icon: FileText },
    { id: 'finance-reports',  label: 'Reports',  icon: ClipboardList },
  ],
  people: [
    { id: 'home', label: 'Home', icon: Home, active: true },
    { type: 'divider' },
    { id: 'people-team',       label: 'Team',       icon: Users },
    { id: 'people-hiring',     label: 'Hiring',     icon: UserPlus },
    { id: 'people-engagement', label: 'Engagement', icon: Smile },
    { id: 'people-org-health', label: 'Org Health', icon: Activity },
  ],
  // Founder / CEO — full top-level nav, every item locked except Home.
  default: [
    { id: 'home',      label: 'Home',     icon: Home, active: true },
    { id: 'org-pulse', label: 'Org Pulse', icon: Activity },
    { type: 'divider' },
    { id: 'marketing', label: 'Marketing', icon: Megaphone },
    { id: 'sales',     label: 'Sales',     icon: TrendingUp },
    { id: 'support',   label: 'Support',   icon: Headphones },
    { id: 'finance',   label: 'Finance',   icon: DollarSign },
    { id: 'people',    label: 'People',    icon: Users },
  ],
};

const SYSTEM_NAV = [
  { id: 'history', label: 'History', icon: HistoryIcon },
  { id: 'agents', label: 'Agents', icon: Bot },
  { id: 'workflows', label: 'Workflows', icon: GitBranch },
  { id: 'knowledge-vault', label: 'Knowledge Vault', icon: BookOpen },
  { id: 'integrations', label: 'Integrations', icon: Plug },
];

/* ─── Section content renderers — read-only cards, one per cardType ─── */

const URGENCY_CLS = {
  'time-sensitive': 'text-amber-400 bg-amber-400/10',
  'due-today':      'text-orange-400 bg-orange-400/10',
  critical:         'text-red-400 bg-red-400/10',
  P0:               'text-red-400 bg-red-400/10',
  P1:               'text-amber-400 bg-amber-400/10',
  P2:               'text-white/55 bg-white/[0.06]',
};

const TagPill = ({ tag, tone = 'neutral' }) => {
  if (!tag) return null;
  const cls = URGENCY_CLS[tag] || (tone === 'positive'
    ? 'text-emerald-400 bg-emerald-400/10'
    : tone === 'warn'
      ? 'text-amber-400 bg-amber-400/10'
      : 'text-indigo-400 bg-indigo-400/10');
  return <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${cls}`}>{tag}</span>;
};

const InboxCard = ({ item }) => (
  <div className="bg-[#0d1b2a] border border-white/[0.06] rounded-lg px-3 py-2" data-testid="onb-card-inbox">
    <div className="flex items-center gap-2">
      <div className="w-5 h-5 rounded-full bg-white/[0.08] text-white/70 flex items-center justify-center text-[9px] font-bold flex-shrink-0">
        {(item.from || '?')[0]?.toUpperCase()}
      </div>
      <p className="text-[12px] font-semibold text-white truncate flex-1 min-w-0">{item.from}</p>
      <TagPill tag={item.tag} />
    </div>
    <p className="text-[11.5px] text-white/85 mt-1 ml-7 truncate">{item.subject}</p>
    {item.preview && <p className="text-[11px] text-white/50 mt-0.5 ml-7 truncate">{item.preview}</p>}
  </div>
);

const MeetingCard = ({ event }) => (
  <div className="bg-[#0d1b2a] border border-white/[0.06] rounded-lg px-3 py-2.5" data-testid="onb-card-meeting">
    <div className="flex items-center gap-3">
      <div className="text-[11px] font-mono font-medium text-indigo-400 w-20 flex-shrink-0">{event.time}</div>
      <p className="text-[12.5px] text-white/90 flex-1 min-w-0 truncate">{event.title}</p>
      <TagPill tag={event.tag} />
    </div>
  </div>
);

const MustDoCard = ({ task, agent, justAdded, onClick }) => {
  const isSetup = task._action === 'open-setup';
  const baseCls = isSetup
    ? 'bg-gradient-to-br from-indigo-500/[0.14] to-purple-600/[0.10] border border-indigo-400/30 hover:from-indigo-500/[0.20] hover:to-purple-600/[0.16] cursor-pointer'
    : 'bg-[#0d1b2a] border border-white/[0.06]';
  return (
    <div
      className={`${baseCls} rounded-lg px-3 py-2.5 transition-colors`}
      style={justAdded ? { animation: 'taskSlideIn 0.45s ease-out' } : undefined}
      data-testid={isSetup ? 'onb-card-setup-task' : 'onb-card-task'}
      onClick={isSetup && onClick ? onClick : undefined}
      role={isSetup ? 'button' : undefined}
    >
      <div className="flex items-center gap-2">
        {isSetup
          ? <Sparkles className="w-4 h-4 text-indigo-300 flex-shrink-0" />
          : (agent?.thumb && <img src={agent.thumb} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" />)}
        <p className={`text-xs font-semibold flex-1 min-w-0 truncate ${isSetup ? 'text-indigo-50' : 'text-white'}`}>{task.title}</p>
        <TagPill tag={task.tag} />
      </div>
      {task.subtitle && <p className={`text-[11px] mt-1 ml-7 line-clamp-1 ${isSetup ? 'text-indigo-200/70' : 'text-white/50'}`}>{task.subtitle}</p>}
    </div>
  );
};

const CampaignCard = ({ item }) => (
  <div className="bg-[#0d1b2a] border border-white/[0.06] rounded-lg px-3 py-2.5" data-testid="onb-card-campaign">
    <div className="flex items-start justify-between gap-2">
      <p className="text-[12.5px] font-semibold text-white truncate flex-1">{item.name}</p>
      <TagPill tag={item.stage} tone="warn" />
    </div>
    <p className="text-[11px] text-white/50 mt-1">{item.audience} · {(item.channels || []).join(' · ')}</p>
  </div>
);

const DealCard = ({ deal }) => (
  <div className="bg-[#0d1b2a] border border-white/[0.06] rounded-lg px-3 py-2.5" data-testid="onb-card-deal">
    <div className="flex items-center gap-2">
      <p className="text-[12.5px] font-semibold text-white flex-1 min-w-0 truncate">{deal.name}</p>
      <span className="text-[11.5px] font-medium text-emerald-300">{deal.value}</span>
    </div>
    <p className="text-[11px] text-white/50 mt-1">{deal.stage}{deal.action ? ` · ${deal.action}` : ''}</p>
  </div>
);

const TicketCard = ({ ticket }) => (
  <div className="bg-[#0d1b2a] border border-white/[0.06] rounded-lg px-3 py-2.5" data-testid="onb-card-ticket">
    <div className="flex items-center gap-2">
      <TagPill tag={ticket.priority} />
      <p className="text-[12.5px] font-semibold text-white flex-1 min-w-0 truncate">{ticket.subject}</p>
    </div>
    <p className="text-[11px] text-white/50 mt-1">{ticket.id} · {ticket.status}</p>
  </div>
);

const FinancialCard = ({ item }) => (
  <div className="bg-[#0d1b2a] border border-white/[0.06] rounded-lg px-3 py-2.5" data-testid="onb-card-financial">
    <div className="flex items-center gap-2">
      <p className="text-[12.5px] font-semibold text-white flex-1 min-w-0 truncate">{item.label}</p>
      <span className="text-[11.5px] font-medium text-emerald-300">{item.amount}</span>
    </div>
    <p className="text-[11px] text-white/50 mt-1">{item.status}</p>
  </div>
);

const PeopleCard = ({ item }) => (
  <div className="bg-[#0d1b2a] border border-white/[0.06] rounded-lg px-3 py-2.5" data-testid="onb-card-people">
    <div className="flex items-center gap-2">
      <p className="text-[12.5px] font-semibold text-white flex-1 min-w-0 truncate">{item.label}</p>
      <TagPill tag={item.status} tone="positive" />
    </div>
    {item.subtitle && <p className="text-[11px] text-white/50 mt-1">{item.subtitle}</p>}
  </div>
);

/* Card-list renderer — picks the right card component per cardType. */
const renderCards = (cardType, payload, agent, onSetupTaskClick) => {
  switch (cardType) {
    case 'inbox':
      return (payload.items || []).map((it, i) => <InboxCard key={i} item={it} />);
    case 'meeting':
      return (payload.events || []).map((e, i) => <MeetingCard key={i} event={e} />);
    case 'task':
      return (payload.tasks || []).map((tk, i) => (
        <MustDoCard
          key={i}
          task={tk}
          agent={agent}
          justAdded={true}
          onClick={tk._action === 'open-setup' ? onSetupTaskClick : undefined}
        />
      ));
    case 'campaign':
      return (payload.campaigns || []).map((c, i) => <CampaignCard key={i} item={c} />);
    case 'deal':
      return (payload.deals || []).map((d, i) => <DealCard key={i} deal={d} />);
    case 'ticket':
      return (payload.tickets || []).map((tk, i) => <TicketCard key={i} ticket={tk} />);
    case 'financial':
      return (payload.items || []).map((it, i) => <FinancialCard key={i} item={it} />);
    case 'people':
      return (payload.items || []).map((it, i) => <PeopleCard key={i} item={it} />);
    default:
      return null;
  }
};

/* Item count helper drives the section count badge. */
const countItems = (cardType, payload) => {
  if (!payload) return 0;
  switch (cardType) {
    case 'inbox':     return (payload.items || []).length;
    case 'meeting':   return (payload.events || []).length;
    case 'task':      return (payload.tasks || []).length;
    case 'campaign':  return (payload.campaigns || []).length;
    case 'deal':      return (payload.deals || []).length;
    case 'ticket':    return (payload.tickets || []).length;
    case 'financial':
    case 'people':    return (payload.items || []).length;
    default:          return 0;
  }
};

/* ─── Chat primitives (V4 conventions) ─── */
const TypingDots = () => (
  <span className="inline-flex items-center gap-1">
    <span className="w-1.5 h-1.5 rounded-full bg-white/45" style={{ animation: 'simDot 1.2s ease-in-out infinite', animationDelay: '0s' }} />
    <span className="w-1.5 h-1.5 rounded-full bg-white/45" style={{ animation: 'simDot 1.2s ease-in-out infinite', animationDelay: '0.2s' }} />
    <span className="w-1.5 h-1.5 rounded-full bg-white/45" style={{ animation: 'simDot 1.2s ease-in-out infinite', animationDelay: '0.4s' }} />
  </span>
);

const ArtifactChip = ({ artifact }) => (
  <div className="flex items-center gap-2 rounded-lg bg-white/[0.06] border border-white/[0.08] px-2.5 py-1.5 mt-2.5 w-fit" data-testid="sim-artifact-chip">
    <div className="w-7 h-7 rounded-md bg-rose-500/[0.15] border border-rose-500/30 flex items-center justify-center flex-shrink-0">
      <FileText className="w-3.5 h-3.5 text-rose-300" />
    </div>
    <div className="min-w-0">
      <p className="text-[12px] text-white/85 leading-tight truncate">{artifact.name}</p>
      <p className="text-[10px] text-white/45">{artifact.size || 'PDF'}</p>
    </div>
  </div>
);

const MemoryPill = ({ docName }) => (
  <div className="mt-3">
    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/[0.08] border border-emerald-500/20 px-2 py-0.5" data-testid="sim-memory-pill">
      <Brain className="w-3 h-3 text-emerald-300" />
      <span className="text-[10.5px] text-emerald-200/85 leading-none">Saved <span className="font-medium">{docName}</span> to memory</span>
    </span>
  </div>
);

const IntegrationCard = ({ tool, state, onConnect }) => (
  <div className="mt-3 rounded-xl border border-indigo-500/30 bg-indigo-500/[0.05] p-3 max-w-md" data-testid="sim-integration-card">
    <div className="flex items-center gap-3">
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: `${tool.accent}20`, border: `1px solid ${tool.accent}55` }}
      >
        <Plug className="w-4 h-4" style={{ color: tool.accent }} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[12px] font-semibold text-white/90">{tool.name}</p>
        <p className="text-[11px] text-white/50">Connect to continue</p>
      </div>
      {state === 'idle' && (
        <button
          onClick={onConnect}
          data-testid="sim-integration-connect"
          className="px-3 py-1.5 rounded-lg text-[11.5px] font-semibold bg-gradient-to-br from-indigo-500 to-purple-600 text-white hover:from-indigo-400 hover:to-purple-500 transition-colors flex-shrink-0"
        >
          Connect
        </button>
      )}
      {state === 'connecting' && (
        <span className="inline-flex items-center gap-1.5 text-[11.5px] text-white/65 flex-shrink-0">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Connecting…
        </span>
      )}
      {state === 'connected' && (
        <span className="inline-flex items-center gap-1.5 text-[11.5px] text-emerald-300/90 font-semibold flex-shrink-0">
          <Check className="w-3.5 h-3.5" /> Connected
        </span>
      )}
    </div>
  </div>
);

const ConnectedSystemRow = ({ tool }) => (
  <div className="flex items-center gap-2 my-3" data-testid="sim-system-connected">
    <span className="h-px flex-1 bg-emerald-400/20" />
    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-emerald-300/80">
      <Check className="w-3 h-3" /> {tool.name} connected
    </span>
    <span className="h-px flex-1 bg-emerald-400/20" />
  </div>
);

/* User bubble (V4 AdHoc convention) — stacks text + artifact in a column. */
const UserMessage = ({ text, artifact }) => (
  <div style={{ marginTop: 28 }}>
    <div className="flex justify-end">
      <div className="max-w-[80%] px-5 py-3.5 rounded-2xl rounded-br-md bg-indigo-500/20 text-white" data-testid="sim-msg-user">
        <p className="text-[14px] leading-[1.7]">{text}</p>
        {artifact && <ArtifactChip artifact={artifact} />}
      </div>
    </div>
  </div>
);

/* Agent message — NO bubble, just avatar + text. The avatar is omitted when
 * `groupedWithPrev` is true (consecutive agent message), so only the first
 * message in a group shows the thumb. */
const AgentMessage = ({ agent, text, memory, integration, integrationState, onConnect, groupedWithPrev }) => (
  <div style={{ marginTop: groupedWithPrev ? 12 : 28 }}>
    <div className="flex items-start gap-3.5">
      {groupedWithPrev
        ? <div className="w-8 flex-shrink-0" />
        : <img src={agent.thumb} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0 mt-0.5" />}
      <div className="min-w-0 flex-1 text-[14px] leading-[1.8] text-white/85" data-testid="sim-msg-agent">
        {text.split('\n').filter(Boolean).map((p, i, arr) => (
          <p key={i} style={{ marginBottom: i < arr.length - 1 ? 16 : 0 }}>{p}</p>
        ))}
        {memory && <MemoryPill docName={memory} />}
        {integration && <IntegrationCard tool={integration} state={integrationState} onConnect={onConnect} />}
      </div>
    </div>
  </div>
);

const TypingMessage = ({ agent, groupedWithPrev }) => (
  <div style={{ marginTop: groupedWithPrev ? 12 : 28 }}>
    <div className="flex items-start gap-3.5">
      {groupedWithPrev
        ? <div className="w-8 flex-shrink-0" />
        : <img src={agent.thumb} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0 mt-0.5" />}
      <div className="text-[14px] leading-[1.8] pt-1">
        <TypingDots />
      </div>
    </div>
  </div>
);

/* ─── Centre-panel chat view (replaces V4ChatPanel during sim) ─── */
const CentreChatView = ({ agent, workflowTitle, script, playedIdx, showTyping, integrationState, onConnect, phase, onBack, message, setMessage }) => {
  const slice = script ? script.turns.slice(0, playedIdx) : [];
  return (
    <div className="flex flex-col h-full" data-testid="centre-chat-view">
      {/* Header — matches V4 "Back to Briefing" pattern */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06] flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 text-[12px] text-white/60 hover:text-white/90 transition-colors"
            data-testid="centre-chat-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Home
          </button>
          <span className="text-white/30">·</span>
          <img src={agent.thumb} alt="" className="w-5 h-5 rounded-full object-cover" />
          <span className="text-[13px] font-semibold text-white/90 truncate">{workflowTitle}</span>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-emerald-300/80 bg-emerald-500/[0.1] border border-emerald-500/20 rounded-full px-2 py-0.5 flex-shrink-0">1 agent active</span>
      </div>

      {/* Transcript */}
      <div className="flex-1 overflow-y-auto px-8 py-6" data-testid="sim-transcript">
        <div className="w-[80%] mx-auto">
          {(() => {
            // Track previous turn's agent-grouping so consecutive agent
            // messages share a single avatar (avatar shown only on first).
            let prevAgentGroup = false;
            return slice.map((turn, i) => {
              if (turn.role === 'user') {
                prevAgentGroup = false;
                return <UserMessage key={i} text={turn.text} artifact={turn.artifact} />;
              }
              if (turn.role === 'system' && turn.kind === 'connected') {
                prevAgentGroup = false;
                return <ConnectedSystemRow key={i} tool={turn.tool} />;
              }
              const grouped = prevAgentGroup;
              prevAgentGroup = true;
              const isLatest = i === slice.length - 1;
              const showLiveCard = turn.integration && isLatest;
              return (
                <AgentMessage
                  key={i}
                  agent={agent}
                  text={turn.text}
                  memory={turn.memory}
                  integration={showLiveCard ? turn.integration : null}
                  integrationState={showLiveCard ? integrationState : 'idle'}
                  onConnect={onConnect}
                  groupedWithPrev={grouped}
                />
              );
            });
          })()}
          {showTyping && <TypingMessage agent={agent} groupedWithPrev={slice.length > 0 && slice[slice.length - 1].role === 'agent'} />}
          {phase === 'complete' && (
            <div className="mt-8 mb-2 flex items-center gap-2 text-[11.5px] text-emerald-300/80" data-testid="sim-complete">
              <Check className="w-3.5 h-3.5" />
              <span>Conversation complete — head back to home to see what {agent.name} delivered.</span>
            </div>
          )}
        </div>
      </div>

      {/* Input bar — V4 ChatPanel style */}
      <div className="flex-shrink-0 px-6 pb-5">
        <div className="w-[80%] mx-auto">
          <div className="bg-[#0d1b2a] border border-white/[0.08] rounded-2xl flex flex-col">
            <div className="px-4 pt-3 pb-2">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={phase !== 'complete'}
                placeholder={phase === 'complete' ? `Message ${agent.name}…` : `${agent.name} is working…`}
                rows={1}
                className="w-full bg-transparent resize-none outline-none text-sm leading-5 text-white/90 placeholder:text-white/30"
                style={{ minHeight: '20px', maxHeight: '160px' }}
                data-testid="sim-chat-input"
              />
            </div>
            <div className="flex items-center justify-between px-3 pb-3">
              <div className="flex items-center gap-1">
                <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg">
                  <img src={agent.thumb} alt="" className="w-5 h-5 rounded-full object-cover" />
                  <span className="text-xs font-medium text-white/85">{agent.name}</span>
                </div>
                <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg opacity-50">
                  <Zap className="w-3.5 h-3.5 text-white/70" />
                  <span className="text-xs font-medium text-white/70">Workflows</span>
                </div>
                <div className="p-1.5 rounded-lg opacity-50">
                  <Paperclip className="w-3.5 h-3.5 text-white/45" />
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center opacity-50">
                  <Mic className="w-4 h-4 text-white/45" />
                </div>
                <button className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${message.trim() ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : 'bg-white/[0.06]'}`}>
                  <Send className={`w-3.5 h-3.5 ${message.trim() ? 'text-white' : 'text-white/45'}`} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Centre-panel briefing view — unified for CEO + dept layouts ─── */

const SectionLabel = ({ icon: Icon, title, count, accent }) => {
  const cls = accent === 'red'      ? 'text-red-400'
            : accent === 'amber'    ? 'text-amber-400'
            : accent === 'emerald'  ? 'text-emerald-400'
            :                          'text-white/50';
  return (
    <div className="flex items-center gap-2 mb-2">
      {Icon && <Icon className={`w-3.5 h-3.5 ${cls}`} />}
      <span className={`text-[11px] font-semibold uppercase tracking-wider ${cls}`}>{title}</span>
      {count != null && count > 0 && (
        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-white/[0.06] text-white/40">{count}</span>
      )}
    </div>
  );
};

const SectionEmpty = ({ copy }) => (
  <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-4 text-center" data-testid="section-empty">
    <p className="text-[11px] text-white/35 leading-relaxed">{copy}</p>
  </div>
);

const SectionBlock = ({ slot, payload, agent, justUnlocked, onSetupTaskClick }) => {
  const cfg = SECTION_CFG[slot.key];
  if (!cfg) return null;
  const label = slot.overrideLabel || cfg.label;
  const count = payload ? countItems(cfg.cardType, payload) : 0;
  return (
    <div
      className="mb-4"
      data-testid={`onb-section-${slot.key}`}
      style={justUnlocked ? { animation: 'tileUnlock 0.6s ease-out' } : undefined}
    >
      <SectionLabel icon={cfg.icon} title={label} count={count} accent={cfg.accent} />
      {payload && count > 0
        ? <div className="flex flex-col gap-2">{renderCards(cfg.cardType, payload, agent, onSetupTaskClick)}</div>
        : <SectionEmpty copy={cfg.emptyCopy} />}
    </div>
  );
};

/* Greeting copy — agent-led, references the most-recent unlocked sections. */
const buildGreeting = (name, agent, unlockedSections) => {
  const fname = name || 'there';
  const keys = Object.keys(unlockedSections);
  if (keys.length === 0) {
    return `Hey ${fname}. I'm ${agent.name} — your starter home is wired up. Pick up where we left off in the chat above the input, or connect more tools to bring the rest of this view to life.`;
  }
  const meetingsCount = countItems('meeting', unlockedSections.calendar);
  const tasksCount    = countItems('task',    unlockedSections.tasks);
  const inboxCount    = countItems('inbox',   unlockedSections.inbox);
  const campaignsCount= countItems('campaign',unlockedSections.campaigns);
  const dealsCount    = countItems('deal',    unlockedSections.pipeline);
  const ticketsCount  = countItems('ticket',  unlockedSections.tickets);
  const finCount      = countItems('financial', unlockedSections.finance);
  const peopleCount   = countItems('people',  unlockedSections.roster);
  const parts = [];
  if (meetingsCount)  parts.push(`${meetingsCount} on your calendar`);
  if (tasksCount)     parts.push(`${tasksCount} must-do${tasksCount > 1 ? 's' : ''}`);
  if (inboxCount)     parts.push(`${inboxCount} in your inbox`);
  if (campaignsCount) parts.push(`${campaignsCount} campaign${campaignsCount > 1 ? 's' : ''} drafted`);
  if (dealsCount)     parts.push(`${dealsCount} deal${dealsCount > 1 ? 's' : ''} queued`);
  if (ticketsCount)   parts.push(`${ticketsCount} ticket${ticketsCount > 1 ? 's' : ''} triaged`);
  if (finCount)       parts.push(`${finCount} finance item${finCount > 1 ? 's' : ''} staged`);
  if (peopleCount)    parts.push(`${peopleCount} people move${peopleCount > 1 ? 's' : ''} ready`);
  const summary = parts.length ? parts.join(' · ') : 'a fresh start';
  return `Hey ${fname}. ${summary}. Connect more tools to bring the rest of this home alive.`;
};

const StatPill = ({ icon: Icon, text, tone }) => {
  const cls = tone === 'good' ? 'text-emerald-400' : tone === 'bad' ? 'text-red-400' : 'text-white/50';
  return (
    <span className={`text-[11px] flex items-center gap-1 ${cls}`}>
      <Icon className="w-3 h-3" /> {text}
    </span>
  );
};

const CentreBriefingView = ({ name, agent, dept, unlockedSections, justUnlockedKey, message, setMessage, onSetupTaskClick }) => {
  const layout = dept === 'default' ? CEO_LAYOUT : (DEPT_LAYOUTS[dept] || CEO_LAYOUT);
  const greetingText = buildGreeting(name, agent, unlockedSections);

  // Stat pills (3 across all layouts) — picked from the most prominent unlocked sections.
  const meetingsCount = countItems('meeting', unlockedSections.calendar);
  const tasksCount    = countItems('task',    unlockedSections.tasks);
  const inboxCount    = countItems('inbox',   unlockedSections.inbox);
  const stats = [
    { icon: CalendarIcon, text: `${meetingsCount} meeting${meetingsCount === 1 ? '' : 's'}`, tone: meetingsCount ? 'good' : 'muted' },
    { icon: ClipboardList, text: `${tasksCount} must-do${tasksCount === 1 ? '' : 's'}`,      tone: tasksCount ? 'good' : 'muted' },
    { icon: Mail,         text: `${inboxCount} inbox`,                                       tone: inboxCount ? 'good' : 'muted' },
  ];

  const gridCls = layout.cols === 4
    ? 'grid grid-cols-4 gap-4 flex-1 min-h-0'
    : 'grid grid-cols-3 gap-5 flex-1 min-h-0';

  return (
    <div className="flex flex-col h-full px-5 py-4" data-testid={`centre-briefing-view-${dept}`}>
      {/* Greeting */}
      <div className="mb-4 flex-shrink-0">
        <div className="flex gap-3">
          <img src={agent.thumb} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-[15px] text-white/70 leading-relaxed">{greetingText}</p>
            <div className="flex items-center gap-3 mt-1.5">
              {stats.map((s, i) => <StatPill key={i} icon={s.icon} text={s.text} tone={s.tone} />)}
            </div>
          </div>
        </div>
      </div>

      {/* Section grid */}
      <div className={gridCls}>
        {layout.columns.map((col, i) => (
          <div key={i} className="flex flex-col min-h-0 overflow-y-auto pr-1">
            {col.map((slot, j) => {
              const cfg = SECTION_CFG[slot.key];
              const payload = cfg?.cardType === 'placeholder' ? null : unlockedSections[slot.key];
              return (
                <SectionBlock
                  key={slot.key}
                  slot={slot}
                  payload={payload}
                  agent={agent}
                  justUnlocked={slot.key === justUnlockedKey}
                  onSetupTaskClick={onSetupTaskClick}
                />
              );
            })}
          </div>
        ))}
      </div>

      {/* Pinned input */}
      <div className="flex-shrink-0 pt-4">
        <div className="bg-[#0d1b2a] border border-white/[0.08] rounded-2xl flex flex-col">
          <div className="px-4 pt-3 pb-2">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={`Message ${agent.name}…`}
              rows={1}
              className="w-full bg-transparent resize-none outline-none text-sm leading-5 text-white/90 placeholder:text-white/30"
              style={{ minHeight: '20px', maxHeight: '160px' }}
              data-testid="briefing-input"
            />
          </div>
          <div className="flex items-center justify-between px-3 pb-3">
            <div className="flex items-center gap-1">
              <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg">
                <img src={agent.thumb} alt="" className="w-5 h-5 rounded-full object-cover" />
                <span className="text-xs font-medium text-white/85">{agent.name}</span>
              </div>
              <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg opacity-50">
                <Zap className="w-3.5 h-3.5 text-white/70" />
                <span className="text-xs font-medium text-white/70">Workflows</span>
              </div>
              <div className="p-1.5 rounded-lg opacity-50">
                <Paperclip className="w-3.5 h-3.5 text-white/45" />
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-8 h-8 rounded-xl flex items-center justify-center opacity-50">
                <Mic className="w-4 h-4 text-white/45" />
              </div>
              <button className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${message.trim() ? 'bg-gradient-to-br from-indigo-500 to-purple-600' : 'bg-white/[0.06]'}`}>
                <Send className={`w-3.5 h-3.5 ${message.trim() ? 'text-white' : 'text-white/45'}`} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


/* ─── Sidebar (mirrors V4Sidebar visuals) ─── */
const Sidebar = ({ collapsed, onToggleCollapse, sidebarItems, name }) => (
  <div className="flex flex-col h-full" data-testid="onboarded-sidebar">
    <div className={`flex items-center ${collapsed ? 'justify-center' : 'justify-end'} px-2 pt-2 pb-1`}>
      <button onClick={onToggleCollapse} className="p-1.5 rounded-lg hover:bg-white/[0.06] transition-colors">
        {collapsed ? <PanelLeftOpen className="w-4 h-4 text-white/40" /> : <PanelLeftClose className="w-4 h-4 text-white/40" />}
      </button>
    </div>
    <nav className="flex-1 overflow-y-auto py-1 px-2 space-y-0.5">
      {sidebarItems.map((it, idx) => {
        if (it.type === 'divider') {
          return <div key={`d-${idx}`} className="border-t border-white/[0.06] my-2 mx-2" />;
        }
        const Icon = it.icon;
        return (
          <button
            key={it.id}
            disabled={!it.active}
            className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'} w-full px-3 py-2 rounded-xl text-[13px] font-medium transition-colors ${it.active ? 'bg-[#0d1b2a] text-white' : 'text-white/40 cursor-not-allowed'}`}
            title={collapsed ? it.label : undefined}
            data-testid={`onboarded-nav-${it.id}`}
          >
            <Icon className="w-4 h-4 flex-shrink-0" />
            {!collapsed && <span className="truncate flex-1 text-left">{it.label}</span>}
            {!collapsed && !it.active && <Lock className="w-3 h-3 flex-shrink-0 text-white/25" />}
          </button>
        );
      })}
      <div className="border-t border-white/[0.06] my-2 mx-2" />
      {SYSTEM_NAV.map((it) => {
        const Icon = it.icon;
        return (
          <button
            key={it.id}
            disabled
            className={`flex items-center ${collapsed ? 'justify-center' : 'gap-3'} w-full px-3 py-2 rounded-xl text-[13px] font-medium text-white/35 cursor-not-allowed`}
            title={collapsed ? it.label : undefined}
            data-testid={`onboarded-nav-${it.id}`}
          >
            <Icon className="w-4 h-4 flex-shrink-0" />
            {!collapsed && <span className="truncate flex-1 text-left">{it.label}</span>}
            {!collapsed && <Lock className="w-3 h-3 flex-shrink-0 text-white/25" />}
          </button>
        );
      })}
    </nav>
    <div className="border-t border-white/[0.06] px-3 py-3">
      {!collapsed ? (
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
            <span className="text-white text-[10px] font-semibold">{(name || 'U')[0].toUpperCase()}</span>
          </div>
          <div className="min-w-0">
            <p className="text-[13px] font-medium truncate text-white/85">{name || 'You'}</p>
            <p className="text-[11px] text-white/40">Starter</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center" title={name}>
            <span className="text-white text-[9px] font-semibold">{(name || 'U')[0].toUpperCase()}</span>
          </div>
        </div>
      )}
    </div>
  </div>
);

/* ─── Main component ─── */
const V4OnboardedHome = ({
  name,
  agentId,
  agentName,
  department,
  tier,
  workflowId,
  workflowTitle,
  onExploreFullDemo,
  onSimulationComplete,
}) => {
  const dept = department && SIDEBAR_BY_DEPT[department] ? department : 'default';
  const sidebarItems = SIDEBAR_BY_DEPT[dept];
  const agent = findAgent(agentId);
  const script = getScript(agentId, workflowId);

  /* View mode: 'chat' (default — sim runs) or 'briefing' (section tiles). */
  const [view, setView] = useState('chat');
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [message, setMessage] = useState('');

  /* Section unlock state — persists across view switches.
   * unlockedSections: { [sectionKey]: payload } where sectionKey is one of
   * SECTION_CFG keys (calendar / tasks / inbox / campaigns / pipeline /
   * tickets / finance / roster). For dept === 'default' (CEO view), the
   * ceoMapper collapses dept-specific sections into inbox/calendar/tasks. */
  const [unlockedSections, setUnlockedSections] = useState({});
  const [justUnlockedKey, setJustUnlockedKey] = useState(null);

  /* Setup-modal state — shows once on first "Back to Home". If the user
   * dismisses it before completing all 5 steps, a synthetic "Complete your
   * setup" task is injected at the top of the Must-Do / Today's Tasks
   * section (clicking it reopens the modal). */
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupShown, setSetupShown] = useState(false); // first-trigger guard
  const [setupDone, setSetupDone] = useState(false);   // user clicked Finish

  /* Sim state. */
  const [playedIdx, setPlayedIdx] = useState(0);
  const [showTyping, setShowTyping] = useState(false);
  const [integrationState, setIntegrationState] = useState('idle');
  const [phase, setPhase] = useState('running');

  const timersRef = useRef([]);
  const completedRef = useRef(false);

  useEffect(() => () => timersRef.current.forEach(clearTimeout), []);

  /* Fire completion once. */
  useEffect(() => {
    if (phase === 'complete' && !completedRef.current) {
      completedRef.current = true;
      onSimulationComplete && onSimulationComplete();
    }
  }, [phase, onSimulationComplete]);

  /* Scheduler. */
  useEffect(() => {
    if (!script) return;
    if (phase !== 'running' && phase !== 'post-connect') return;
    const turns = script.turns;
    if (playedIdx >= turns.length) { setPhase('complete'); return; }
    const next = turns[playedIdx];

    if (next.role === 'user') {
      const tid = setTimeout(() => setPlayedIdx((i) => i + 1), 600);
      timersRef.current.push(tid);
      return;
    }
    if (next.role === 'system' && next.kind === 'connected') {
      const tid = setTimeout(() => setPlayedIdx((i) => i + 1), 700);
      timersRef.current.push(tid);
      return;
    }
    setShowTyping(true);
    const ms = 900 + Math.min(1100, (next.text || '').length * 11);
    const tid = setTimeout(() => {
      setShowTyping(false);
      setPlayedIdx((i) => i + 1);
      if (next.integration) setPhase('awaiting-connect');
    }, ms);
    timersRef.current.push(tid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, playedIdx, script]);

  const handleConnect = () => {
    if (integrationState !== 'idle') return;
    setIntegrationState('connecting');
    const t1 = setTimeout(() => {
      setIntegrationState('connected');
      // Build unlocked-sections map from script.homeActions (CEO mapper applied
      // when dept === 'default'). Sections cascade in one-by-one with 350ms
      // stagger so the user perceives a sequenced reveal instead of a wall
      // of data appearing at once. Each section fires its own tileUnlock
      // animation via the justUnlockedKey state.
      if (script) {
        const isDefault = dept === 'default';
        const sections = buildUnlockedSections(script, isDefault);
        const keys = Object.keys(sections);
        const STAGGER_MS = 350;
        keys.forEach((key, idx) => {
          const t = setTimeout(() => {
            setUnlockedSections((prev) => ({ ...prev, [key]: sections[key] }));
            setJustUnlockedKey(key);
            const tClear = setTimeout(() => {
              // Clear only if this is still the most-recently-unlocked key.
              setJustUnlockedKey((cur) => (cur === key ? null : cur));
            }, 1100);
            timersRef.current.push(tClear);
          }, idx * STAGGER_MS);
          timersRef.current.push(t);
        });
      }
      const t2 = setTimeout(() => setPhase('post-connect'), 700);
      timersRef.current.push(t2);
    }, 1300);
    timersRef.current.push(t1);
  };

  const handleBackToHome = () => {
    setView('briefing');
    if (!setupShown) {
      setSetupShown(true);
      // Slight delay so the briefing view paints first; modal feels like a
      // post-arrival prompt rather than a blocker on transition.
      setTimeout(() => setSetupOpen(true), 450);
    }
  };

  /* Derived sections — injects a synthetic "Complete your setup" task at
   * the top of Must-Do / Today's Tasks when the user has dismissed the
   * setup modal without finishing it. Click on the task reopens the modal. */
  const displayedSections = useMemo(() => {
    if (setupDone || !setupShown) return unlockedSections;
    if (setupOpen) return unlockedSections; // don't add while modal is open
    const setupTask = {
      title: 'Complete your setup',
      subtitle: 'Finish the 5 quick steps to unlock the rest of your home',
      tag: 'Setup',
      _action: 'open-setup',
    };
    const existingTasks = unlockedSections.tasks?.tasks || [];
    return {
      ...unlockedSections,
      tasks: { tasks: [setupTask, ...existingTasks] },
    };
  }, [unlockedSections, setupDone, setupShown, setupOpen]);

  return (
    <div className="fixed inset-0 aurora-gradient overflow-hidden" data-testid="onboarded-home-root">
      {/* Top banner */}
      <div
        className="absolute top-0 left-0 right-0 flex items-center justify-between gap-3 px-6 h-12 bg-gradient-to-r from-indigo-500/[0.18] via-purple-500/[0.14] to-indigo-500/[0.18] border-b border-indigo-500/25 backdrop-blur-sm z-30"
        data-testid="onboarded-home-banner"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="w-3.5 h-3.5 text-indigo-300 flex-shrink-0" />
          <p className="text-[12.5px] text-white/85 truncate">
            <span className="font-semibold">This is your starter home.</span> See what Implify can do with a fully wired team.
          </p>
        </div>
        <button
          onClick={onExploreFullDemo}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11.5px] font-semibold bg-gradient-to-br from-indigo-500 to-purple-600 text-white hover:from-indigo-400 hover:to-purple-500 transition-colors flex-shrink-0"
          data-testid="onboarded-home-banner-cta"
        >
          Tour the full demo <ArrowRight className="w-3 h-3" />
        </button>
      </div>

      {/* Logo (top-left, below banner) */}
      <div className="absolute top-[68px] left-5 z-40">
        <img src="/implify-logo-white.png" alt="Implify" className="h-7" />
      </div>

      {/* Top-right glass panel — date · theme · bell · avatar */}
      <div className="absolute top-[64px] right-4 z-40 flex items-center gap-3">
        <div className="bg-white/[0.05] border border-white/[0.10] backdrop-blur-md rounded-xl flex items-center gap-3 px-4" style={{ height: '40px' }}>
          <span className="text-[11px] font-mono text-white/85">Mon, Jan 27 · 9:02 AM</span>
          <div className="h-4 w-px bg-white/[0.1]" />
          <Moon className="w-4 h-4 text-white cursor-default" />
          <Sun className="w-4 h-4 text-white/50 cursor-default" />
          <div className="relative">
            <Bell className="w-4 h-4 text-white/60 cursor-default" />
          </div>
          <div className="flex items-center gap-2 cursor-default pl-2 border-l border-white/[0.1]">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <span className="text-white text-[10px] font-semibold">{(name || 'U')[0].toUpperCase()}</span>
            </div>
            <ChevronDown className="w-3.5 h-3.5 text-white/40" />
          </div>
        </div>
      </div>

      {/* Sidebar (glass) */}
      <div
        className="bg-white/[0.03] border border-white/[0.08] backdrop-blur-md absolute left-3 bottom-3 rounded-2xl z-20 overflow-hidden transition-all duration-300"
        style={{ width: navCollapsed ? '60px' : '220px', top: '116px' }}
        data-testid="onboarded-home-sidebar"
      >
        <Sidebar collapsed={navCollapsed} onToggleCollapse={() => setNavCollapsed((c) => !c)} sidebarItems={sidebarItems} name={name} />
      </div>

      {/* Centre panel (glass) */}
      <div
        className="bg-white/[0.03] border border-white/[0.08] backdrop-blur-md absolute bottom-3 rounded-2xl z-20 overflow-hidden transition-all duration-300"
        style={{ left: navCollapsed ? 'calc(60px + 24px)' : 'calc(220px + 24px)', top: '116px', right: '12px' }}
        data-testid="onboarded-home-center"
      >
        {view === 'chat' ? (
          <CentreChatView
            agent={agent}
            workflowTitle={workflowTitle}
            script={script}
            playedIdx={playedIdx}
            showTyping={showTyping}
            integrationState={integrationState}
            onConnect={handleConnect}
            phase={phase}
            onBack={handleBackToHome}
            message={message}
            setMessage={setMessage}
          />
        ) : (
          <CentreBriefingView
            name={name}
            agent={agent}
            dept={dept}
            unlockedSections={displayedSections}
            justUnlockedKey={justUnlockedKey}
            message={message}
            setMessage={setMessage}
            onSetupTaskClick={() => setSetupOpen(true)}
          />
        )}
      </div>

      <style>{`
        @keyframes simDot { 0%, 100% { opacity: 0.3 } 50% { opacity: 1 } }
        @keyframes tileUnlock {
          0%   { transform: scale(0.985); box-shadow: 0 0 0 0 rgba(129, 140, 248, 0.45); }
          60%  { transform: scale(1.012); box-shadow: 0 0 0 18px rgba(129, 140, 248, 0); }
          100% { transform: scale(1); }
        }
        @keyframes taskSlideIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* Setup Modal — opens once on first Back-to-Home; clicking the
       * synthetic "Complete your setup" task in Must-Do reopens it. */}
      <SetupModal
        open={setupOpen}
        onClose={() => setSetupOpen(false)}
        onFinish={() => { setSetupDone(true); setSetupOpen(false); }}
        tier={tier}
      />
    </div>
  );
};

export default V4OnboardedHome;
